import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Spinner } from 'flowbite-react';
import {
  FiSend,
  FiPlus,
  FiCheck,
  FiX,
  FiMessageSquare,
  FiCpu,
  FiTrash2,
  FiRefreshCw,
  FiZap,
  FiAlertTriangle,
  FiCopy,
} from 'react-icons/fi';
import {
  useConversation,
  ConversationMessage,
  StoredContentBlock,
} from '../../hooks/useConversation';
import ReactMarkdown from 'react-markdown';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/// Parse a tool result (JSON string) and extract an entity link if applicable.
function entityLinkFromResult(_toolName: string, content: string, workspaceId: string): { label: string; path: string } | null {
  try {
    const parsed = JSON.parse(content);
    const id = parsed.id;
    if (!id) return null;

    const entityType = parsed.created ?? parsed.updated;
    if (!entityType) return null;

    const routeMap: Record<string, { prefix: string; label: string }> = {
      task: { prefix: 'tasks', label: 'View task' },
      outcome: { prefix: 'outcomes', label: 'View outcome' },
      decision: { prefix: 'decisions', label: 'View decision' },
      person: { prefix: 'people', label: 'View person' },
    };

    const mapping = routeMap[entityType];
    if (!mapping) return null;

    return { label: mapping.label, path: `/workspaces/${workspaceId}/${mapping.prefix}/${id}` };
  } catch {
    return null;
  }
}

const ConversationListItem: React.FC<{
  conv: { id: string; title: string | null; created_at: number };
  active: boolean;
  onClick: () => void;
  onDelete: () => void;
}> = ({ conv, active, onClick, onDelete }) => (
  <div
    className={`group flex items-center rounded-lg transition-colors ${
      active
        ? 'bg-cm-accent-orange/20 text-cm-accent-orange'
        : 'text-cm-text-secondary hover:bg-cm-bg-hover'
    }`}
  >
    <button
      onClick={onClick}
      className="flex-1 text-left px-3 py-2 text-sm truncate min-w-0"
    >
      <FiMessageSquare className="inline mr-2 opacity-50" size={14} />
      {conv.title ?? new Date(conv.created_at).toLocaleDateString()}
    </button>
    <button
      onClick={e => {
        e.stopPropagation();
        onDelete();
      }}
      className="p-1.5 mr-1 rounded opacity-0 group-hover:opacity-60 hover:!opacity-100 hover:text-cm-accent-pink transition-all"
      title="Delete conversation"
    >
      <FiTrash2 size={12} />
    </button>
  </div>
);

const ToolCallCard: React.FC<{
  block: StoredContentBlock & { type: 'tool_use' };
  result?: StoredContentBlock & { type: 'tool_result' };
  workspaceId: string;
  onApprove?: (toolUseId: string) => void;
  onReject?: (toolUseId: string) => void;
}> = ({ block, result, workspaceId, onApprove, onReject }) => {
  const [expanded, setExpanded] = useState(false);
  const needsApproval = block.requires_approval && !result;

  const entityLink = result ? entityLinkFromResult(block.name, result.content, workspaceId) : null;

  return (
    <>
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => setExpanded(true)}
          className={`flex items-center gap-1.5 text-xs text-cm-text-muted bg-cm-bg-deep hover:bg-cm-bg-hover rounded px-2 py-1 transition-colors cursor-pointer ${
            needsApproval ? 'border border-cm-accent-orange/50' : ''
          }`}
        >
          <FiCpu size={11} className="shrink-0" />
          <span className="font-mono">{block.name}</span>
          {result && (
            <span className={`ml-1 ${result.is_error ? 'text-cm-accent-pink' : 'text-cm-accent-green'}`}>
              {result.is_error ? 'error' : 'ok'}
            </span>
          )}
          {needsApproval && (
            <span className="ml-1 text-cm-accent-orange">awaiting approval</span>
          )}
          {!block.requires_approval && !result && (
            <span className="ml-1 animate-pulse">...</span>
          )}
        </button>
        {needsApproval && onApprove && onReject && (
          <>
            <button
              onClick={() => onApprove(block.tool_use_id)}
              className="w-5 h-5 flex items-center justify-center rounded-full bg-cm-accent-green/20 text-cm-accent-green hover:bg-cm-accent-green/30 transition-colors"
              title="Approve"
            >
              <FiCheck size={12} />
            </button>
            <button
              onClick={() => onReject(block.tool_use_id)}
              className="w-5 h-5 flex items-center justify-center rounded-full bg-cm-accent-pink/20 text-cm-accent-pink hover:bg-cm-accent-pink/30 transition-colors"
              title="Reject"
            >
              <FiX size={12} />
            </button>
          </>
        )}
        {entityLink && (
          <Link
            to={entityLink.path}
            className="text-[11px] text-cm-accent-orange hover:underline"
          >
            {entityLink.label} &rarr;
          </Link>
        )}
      </div>

      {/* Detail modal */}
      {expanded && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setExpanded(false)}>
          <div
            className="bg-cm-bg-surface border border-cm-border-subtle rounded-xl p-5 max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-mono text-sm text-cm-accent-orange">{block.name}</h3>
              <button onClick={() => setExpanded(false)} className="text-cm-text-muted hover:text-cm-text-primary">
                <FiX size={16} />
              </button>
            </div>

            <div className="space-y-3 overflow-y-auto">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-cm-text-muted mb-1">Input</div>
                <pre className="text-xs bg-cm-bg-deep rounded-lg p-3 overflow-x-auto text-cm-text-secondary font-mono">
                  {JSON.stringify(block.input, null, 2)}
                </pre>
              </div>

              {result && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-cm-text-muted mb-1">
                    Result {result.is_error && <span className="text-cm-accent-pink">(error)</span>}
                  </div>
                  <pre className={`text-xs bg-cm-bg-deep rounded-lg p-3 overflow-x-auto font-mono whitespace-pre-wrap ${
                    result.is_error ? 'text-cm-accent-pink' : 'text-cm-text-secondary'
                  }`}>
                    {(() => {
                      try { return JSON.stringify(JSON.parse(result.content), null, 2); }
                      catch { return result.content; }
                    })()}
                  </pre>
                  {(() => {
                    const link = entityLinkFromResult(block.name, result.content, workspaceId);
                    if (!link) return null;
                    return (
                      <Link
                        to={link.path}
                        className="inline-flex items-center gap-1 mt-2 text-xs text-cm-accent-orange hover:underline"
                      >
                        {link.label} &rarr;
                      </Link>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const InferenceErrorCard: React.FC<{
  block: StoredContentBlock & { type: 'error' };
}> = ({ block }) => {
  const [copied, setCopied] = useState(false);

  const copyRef = async () => {
    try {
      await navigator.clipboard.writeText(block.correlation_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API unavailable — silently ignore.
    }
  };

  return (
    <div className="flex justify-start mb-3">
      <div className="max-w-[80%] rounded-xl px-4 py-3 text-sm bg-cm-accent-pink/10 border border-cm-accent-pink/40 text-cm-text-primary">
        <div className="flex items-start gap-2">
          <FiAlertTriangle size={14} className="shrink-0 mt-0.5 text-cm-accent-pink" />
          <div className="flex-1 min-w-0">
            <div className="text-cm-text-primary">{block.message}</div>
            <div className="mt-2 flex items-center gap-2 text-[11px] text-cm-text-muted">
              {block.error_code && (
                <span className="font-mono bg-cm-bg-deep px-1.5 py-0.5 rounded">
                  {block.error_code}
                </span>
              )}
              <button
                onClick={copyRef}
                className="inline-flex items-center gap-1 font-mono hover:text-cm-accent-orange transition-colors"
                title="Copy reference ID to share with support"
              >
                <FiCopy size={10} />
                <span>ref: {block.correlation_id}</span>
                {copied && <span className="text-cm-accent-green">copied</span>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MessageBubble: React.FC<{
  msg: ConversationMessage;
  allMessages: ConversationMessage[];
  workspaceId: string;
  onApproveToolCall?: (toolUseId: string) => void;
  onRejectToolCall?: (toolUseId: string) => void;
}> = ({ msg, allMessages, workspaceId, onApproveToolCall, onRejectToolCall }) => {
  const isUser = msg.role.type === 'user';
  const isAssistant = msg.role.type === 'assistant';
  const isPendingActions = msg.role.type === 'pending_actions';
  const isSummary = msg.role.type === 'summary';

  if (isPendingActions) return null;

  // Summary messages render as a centered annotation divider
  if (isSummary) {
    return (
      <div className="flex items-center gap-3 my-4">
        <div className="flex-1 h-px bg-cm-border-subtle" />
        <span className="text-[11px] text-cm-text-muted italic">
          Previous conversation summarized
        </span>
        <div className="flex-1 h-px bg-cm-border-subtle" />
      </div>
    );
  }

  const textBlocks = msg.content.filter(
    (b): b is StoredContentBlock & { type: 'text' } => b.type === 'text',
  );
  const toolUseBlocks = msg.content.filter(
    (b): b is StoredContentBlock & { type: 'tool_use' } => b.type === 'tool_use',
  );
  const toolResultBlocks = msg.content.filter(
    (b): b is StoredContentBlock & { type: 'tool_result' } => b.type === 'tool_result',
  );
  const errorBlocks = msg.content.filter(
    (b): b is StoredContentBlock & { type: 'error' } => b.type === 'error',
  );

  // Build tool result lookup for matching results to tool_use blocks
  const toolResultMap = new Map<string, StoredContentBlock & { type: 'tool_result' }>();
  if (isAssistant && toolUseBlocks.length > 0) {
    for (const m of allMessages) {
      for (const block of m.content) {
        if (block.type === 'tool_result') {
          toolResultMap.set(block.tool_use_id, block);
        }
      }
    }
  }

  // User messages that are only tool results — hidden (shown on the tool_use card instead)
  const isToolResultOnly = isUser && textBlocks.length === 0 && toolResultBlocks.length > 0;
  if (isToolResultOnly) return null;

  return (
    <>
      {/* Inference error cards */}
      {errorBlocks.map((b, i) => (
        <InferenceErrorCard key={`err-${i}`} block={b} />
      ))}

      {/* Tool calls rendered as centered muted indicators, outside any bubble */}
      {toolUseBlocks.length > 0 && (
        <div className="flex flex-wrap justify-center gap-2 my-2">
          {toolUseBlocks.map((b, i) => (
            <ToolCallCard key={i} block={b} result={toolResultMap.get(b.tool_use_id)} workspaceId={workspaceId} onApprove={onApproveToolCall} onReject={onRejectToolCall} />
          ))}
        </div>
      )}

      {/* Text content in a bubble */}
      {textBlocks.length > 0 && (
        <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
          <div
            className={`max-w-[80%] rounded-xl px-4 py-3 text-sm ${
              isUser
                ? 'bg-cm-accent-orange/20 text-cm-text-primary'
                : 'bg-cm-bg-elevated text-cm-text-primary border border-cm-border-subtle'
            }`}
          >
            {textBlocks.map((b, i) => (
              <div key={i} className="prose prose-sm prose-invert max-w-none">
                <ReactMarkdown>{b.text}</ReactMarkdown>
              </div>
            ))}

            {isAssistant && msg.bedrock_metadata && (
              <div className="mt-2 text-[10px] text-cm-text-muted opacity-50">
                {msg.bedrock_metadata.input_tokens + msg.bedrock_metadata.output_tokens} tokens
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const AssistantPage: React.FC = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const {
    conversations,
    conversationsLoaded,
    activeConversation,
    messages,
    loading,
    sending,
    error,
    listConversations,
    createConversation,
    selectConversation,
    sendMessage,
    refreshMessages,
    approveToolCall,
    rejectToolCall,
    deleteConversation,
    waitingForResponse,
    contextInfo,
    compacting,
    compactConversation,
  } = useConversation(workspaceId ?? '');

  const [input, setInput] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load conversations on mount
  useEffect(() => {
    listConversations();
  }, [listConversations]);

  // Auto-select the most recent conversation when the list loads and nothing is selected
  useEffect(() => {
    if (!activeConversation && conversations.length > 0) {
      selectConversation(conversations[conversations.length - 1]);
    }
  }, [conversations, activeConversation, selectConversation]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    await sendMessage(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewConversation = async () => {
    await createConversation();
  };

  const isInferring = sending || waitingForResponse || activeConversation?.status.type === 'inferring';

  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex h-[calc(100vh-4rem)] max-w-6xl mx-auto gap-0 md:gap-4">
      {/* ── Mobile sidebar toggle ──────────────────────────────── */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="md:hidden fixed top-16 left-2 z-40 p-2 rounded-lg bg-cm-bg-surface border border-cm-border-subtle text-cm-text-muted hover:text-cm-accent-orange transition-colors"
      >
        <FiMessageSquare size={18} />
      </button>

      {/* ── Sidebar: conversation list ──────────────────────────── */}
      <div
        className={`
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
          fixed md:static inset-y-0 left-0 z-30
          w-64 md:w-56 shrink-0
          flex flex-col
          border-r border-cm-border-subtle pr-4 pl-4 md:pl-0 pt-16 md:pt-0
          bg-cm-bg-deep md:bg-transparent
          transition-transform duration-200 ease-in-out
        `}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-cm-text-secondary uppercase tracking-wider">
            Chats
          </h2>
          <button
            onClick={handleNewConversation}
            className="p-1 rounded-md text-cm-text-muted hover:text-cm-accent-orange hover:bg-cm-bg-hover transition-colors"
            title="New conversation"
          >
            <FiPlus size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto space-y-1">
          {conversations.map(conv => (
            <ConversationListItem
              key={conv.id}
              conv={conv}
              active={conv.id === activeConversation?.id}
              onClick={() => {
                selectConversation(conv);
                setSidebarOpen(false);
              }}
              onDelete={() => setConfirmDeleteId(conv.id)}
            />
          ))}
          {!conversationsLoaded && !error && (
            <div className="flex justify-center py-6">
              <Spinner size="sm" />
            </div>
          )}
          {conversationsLoaded && conversations.length === 0 && !error && (
            <p className="text-xs text-cm-text-muted px-2 py-4">
              No conversations yet. Start a new one!
            </p>
          )}
          {conversations.length === 0 && error && (
            <p className="text-xs text-cm-accent-pink px-2 py-4">
              Failed to load conversations: {error}
            </p>
          )}
        </div>
      </div>

      {/* ── Mobile backdrop ────────────────────────────────────── */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 z-20 bg-black/40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Delete confirmation overlay ────────────────────────── */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-cm-bg-surface border border-cm-border-subtle rounded-xl p-6 max-w-sm mx-4 shadow-xl">
            <h3 className="text-sm font-semibold text-cm-text-primary mb-2">
              Delete conversation?
            </h3>
            <p className="text-xs text-cm-text-secondary mb-4">
              This will permanently delete the conversation and all its messages. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="px-3 py-1.5 rounded-lg text-sm text-cm-text-secondary hover:bg-cm-bg-hover transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await deleteConversation(confirmDeleteId);
                  setConfirmDeleteId(null);
                }}
                className="px-3 py-1.5 rounded-lg text-sm bg-cm-accent-pink/20 text-cm-accent-pink hover:bg-cm-accent-pink/30 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Main chat area ─────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {activeConversation ? (
          <>
            {/* Header */}
            <div className="flex items-center gap-2 pb-3 border-b border-cm-border-subtle mb-3">
              <h1 className="text-lg font-mono font-semibold text-cm-text-primary truncate">
                {activeConversation.title ?? 'Untitled'}
              </h1>
              {isInferring && (
                <span className="flex items-center gap-1 text-xs text-cm-accent-orange">
                  <Spinner size="xs" /> thinking...
                </span>
              )}
              {activeConversation?.status.type === 'awaiting_approval' && (
                <span className="text-xs text-cm-accent-gold bg-cm-accent-gold/10 px-2 py-0.5 rounded-full">
                  awaiting approval
                </span>
              )}
              <div className="ml-auto flex items-center gap-2">
                {/* Context usage bar */}
                {contextInfo && contextInfo.estimated_tokens_used > 0 && (
                  <div className="flex items-center gap-1.5" title={`${contextInfo.estimated_tokens_used.toLocaleString()} / ${contextInfo.context_window.toLocaleString()} tokens`}>
                    <div className="w-16 h-1.5 bg-cm-bg-deep rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          contextInfo.usage_ratio > 0.8
                            ? 'bg-cm-accent-pink'
                            : contextInfo.usage_ratio > 0.5
                              ? 'bg-cm-accent-gold'
                              : 'bg-cm-accent-green'
                        }`}
                        style={{ width: `${Math.min(contextInfo.usage_ratio * 100, 100)}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-cm-text-muted">
                      {Math.round(contextInfo.usage_ratio * 100)}%
                    </span>
                  </div>
                )}
                {/* Compact button */}
                <button
                  onClick={compactConversation}
                  disabled={compacting || messages.length < 4}
                  className="p-1 rounded-md text-cm-text-muted hover:text-cm-accent-orange hover:bg-cm-bg-hover transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Compact conversation (summarize history)"
                >
                  {compacting ? <Spinner size="xs" /> : <FiZap size={14} />}
                </button>
                {/* Refresh */}
                <button
                  onClick={refreshMessages}
                  className="p-1 rounded-md text-cm-text-muted hover:text-cm-accent-orange hover:bg-cm-bg-hover transition-colors"
                  title="Refresh messages"
                >
                  <FiRefreshCw size={14} />
                </button>
                <span className="text-[10px] text-cm-text-muted font-mono">
                  {activeConversation.model_id.split('.').pop()}
                </span>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto pr-2">
              {loading ? (
                <div className="flex justify-center py-12">
                  <Spinner size="lg" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-cm-text-muted">
                  <FiMessageSquare size={32} className="mb-2 opacity-30" />
                  <p className="text-sm">Send a message to get started</p>
                </div>
              ) : (
                <>
                  {messages.map(msg => (
                    <MessageBubble key={msg.id} msg={msg} allMessages={messages} workspaceId={workspaceId ?? ''} onApproveToolCall={approveToolCall} onRejectToolCall={rejectToolCall} />
                  ))}
                  {isInferring && (
                    <div className="flex justify-start mb-3">
                      <div className="bg-cm-bg-elevated border border-cm-border-subtle rounded-xl px-4 py-3 flex items-center gap-2 text-sm text-cm-text-muted">
                        <div className="flex gap-1">
                          <span className="w-1.5 h-1.5 bg-cm-accent-orange rounded-full animate-bounce [animation-delay:0ms]" />
                          <span className="w-1.5 h-1.5 bg-cm-accent-orange rounded-full animate-bounce [animation-delay:150ms]" />
                          <span className="w-1.5 h-1.5 bg-cm-accent-orange rounded-full animate-bounce [animation-delay:300ms]" />
                        </div>
                        <span>Thinking...</span>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Input area */}
            <div className="pt-3 border-t border-cm-border-subtle">
              {error && (
                <div className="text-xs text-cm-accent-pink bg-cm-accent-pink/10 rounded-lg px-3 py-2 mb-2">
                  {error}
                </div>
              )}
              <div className="flex gap-2">
                <textarea
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={activeConversation?.status.type === 'awaiting_approval' ? 'Resolve pending actions first...' : 'Ask the assistant...'}
                  disabled={sending || activeConversation?.status.type === 'awaiting_approval'}
                  rows={1}
                  className="flex-1 resize-none rounded-xl border border-cm-border-subtle bg-cm-bg-surface px-4 py-2.5 text-sm text-cm-text-primary placeholder:text-cm-text-muted focus:outline-none focus:border-cm-accent-orange/50 disabled:opacity-50"
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || sending || activeConversation?.status.type === 'awaiting_approval'}
                  className="px-4 py-2 rounded-xl bg-cm-accent-orange text-white text-sm font-medium hover:bg-cm-accent-orange/80 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  {sending ? <Spinner size="sm" /> : <FiSend size={16} />}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-cm-text-muted">
            <FiCpu size={48} className="mb-4 opacity-20" />
            <p className="text-lg font-mono mb-2">Chronomap Assistant</p>
            <p className="text-sm mb-6">Ask questions about your workspace or request changes.</p>
            <button
              onClick={handleNewConversation}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-cm-accent-orange text-white text-sm font-medium hover:bg-cm-accent-orange/80 transition-colors"
            >
              <FiPlus size={14} />
              New conversation
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssistantPage;
